/*
 * Pictures from the live camera, and the small copies the gallery shows.
 *
 * Kept apart from the camera screen so its tests can stand in for both:
 * jsdom has no canvas to draw on.
 */
import { brightenPixels } from './camera/controls';
import { BURST, captureBurst } from './camera/capture';
import { grayFromRgba, sharpness } from './spores/focus';
import { frameSharpness, tileSharpness } from './zstack/select';
import { ENCIRCLE, cropSize, frameRect } from './ai/encircle';

/** Resolves once the video has a frame, or after `waitMs` without one. A
 *  phone's camera takes a second or so to start, and a Snap tapped in that
 *  second should take the picture, not refuse it. */
function firstFrame(video, waitMs) {
  if (!video || video.videoWidth || waitMs <= 0) return Promise.resolve();
  return new Promise(resolve => {
    const events = ['loadeddata', 'resize', 'playing'];
    const done = () => {
      clearInterval(poll);
      clearTimeout(limit);
      events.forEach(name => video.removeEventListener?.(name, check));
      resolve();
    };
    const check = () => { if (video.videoWidth) done(); };
    // Events where the browser sends them, a poll where it does not.
    events.forEach(name => video.addEventListener?.(name, check));
    const poll = setInterval(check, 100);
    const limit = setTimeout(done, waitMs);
  });
}

/** The video's current frame as a JPEG, its longest side at most `maxSide`,
 *  waiting up to `waitMs` for the first frame of a camera still starting.
 *  `gain` is the camera controls' digital brightness (utils/camera/controls.js),
 *  so the picture is what the preview showed. Async, so a canvas that throws
 *  is a rejection like any other failure. */
export async function grabFrame(video, { maxSide = 2048, quality = 0.88, waitMs = 3000, gain = 1 } = {}) {
  await firstFrame(video, waitMs);
  return encode(drawFrame(video, { maxSide, gain }), quality);
}

/** The video's current frame drawn on a canvas, its longest side at most `maxSide`, brightened by `gain`. */
function drawFrame(video, { maxSide, gain }) {
  const width = video?.videoWidth;
  const height = video?.videoHeight;
  if (!width || !height) throw new Error('camera-not-ready');
  const scale = Math.min(1, maxSide / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const ctx = canvas.getContext('2d');
  const brighter = gain > 0 && gain !== 1;
  // The canvas's own filter where it has one (Chrome, Firefox); the pixels by hand where not (older Safari).
  const filtered = brighter && 'filter' in ctx;
  if (filtered) ctx.filter = `brightness(${gain})`;
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  if (brighter && !filtered) {
    const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
    brightenPixels(image.data, gain);
    ctx.putImageData(image, 0, 0);
  }
  return canvas;
}

function encode(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('camera-not-ready'))), 'image/jpeg', quality);
  });
}

//: the width a burst's frames are scored at: the focus meter's own (components/camera/useFocusMeter.js)
const SCORE_WIDTH = 480;

/** A drawn frame's grey levels, SCORE_WIDTH wide as the focus meter reads the live picture; null without a canvas. */
function grayOf(canvas) {
  const w = SCORE_WIDTH;
  const h = Math.max(1, Math.round((canvas.height * w) / canvas.width));
  const small = document.createElement('canvas');
  small.width = w;
  small.height = h;
  const ctx = small.getContext('2d', { willReadFrequently: true });
  if (!ctx?.getImageData) return null;
  ctx.drawImage(canvas, 0, 0, w, h);
  return { gray: grayFromRgba(ctx.getImageData(0, 0, w, h).data, w, h), w, h };
}

/** How sharp a drawn frame is (utils/spores/focus.js), read as the focus meter reads. */
function sharpnessOf(canvas) {
  const read = grayOf(canvas);
  return read ? sharpness(read.gray, read.w, read.h) : 0;
}

/**
 * One layer of a Z-stack (utils/zstack/engine.js): the frame drawn at full
 * size, its sharpness per tile (utils/zstack/select.js; `ring` leaves an
 * eyepiece's dark ring out) read SCORE_WIDTH wide, and the frame encoded as
 * grabFrame's JPEG -- the full-size canvas let go at once, so a stack of 40
 * holds JPEGs, not pixels. {blob, tiles, sharpness}.
 */
export async function grabLayer(video, { maxSide = 2048, quality = 0.88, gain = 1, ring = false } = {}) {
  const canvas = drawFrame(video, { maxSide, gain });
  try {
    const read = grayOf(canvas);
    const tiles = read ? tileSharpness(read.gray, read.w, read.h, { ring }) : [];
    return { blob: await encode(canvas, quality), tiles, sharpness: frameSharpness(tiles) };
  } finally {
    canvas.width = 0;
    canvas.height = 0;
  }
}

/**
 * The encircle (utils/ai/encircle.js): the frame frozen at the moment the
 * loop was drawn, and the part of it the loop names. `bounds` is the loop's
 * box on `viewport` (the video shown with object-fit cover). The still is
 * grabFrame's JPEG, for the screen to hold while the AI answers and to keep as
 * a photo; the crop is at most `cropMax` px on its longer side, re-encoded by
 * the canvas (no EXIF), for POST /ai/identify. {still, crop, rect, frame}.
 */
export async function grabEncircle(video, { bounds, viewport }, {
  maxSide = 2048, cropMax = ENCIRCLE.cropMax, quality = 0.88, waitMs = 3000, gain = 1,
} = {}) {
  await firstFrame(video, waitMs);
  const canvas = drawFrame(video, { maxSide, gain });
  try {
    const frame = { width: canvas.width, height: canvas.height };
    const rect = frameRect(bounds, viewport, frame);
    if (!rect) throw new Error('encircle-outside');
    const size = cropSize(rect, cropMax);
    const crop = document.createElement('canvas');
    crop.width = size.width;
    crop.height = size.height;
    crop.getContext('2d').drawImage(canvas, rect.x, rect.y, rect.w, rect.h, 0, 0, size.width, size.height);
    try {
      return { still: await encode(canvas, quality), crop: await encode(crop, 0.9), rect, frame };
    } finally {
      crop.width = 0;
      crop.height = 0;
    }
  } finally {
    canvas.width = 0;
    canvas.height = 0;
  }
}

/**
 * A burst (the Pro drawer's capture mode, utils/camera/capture.js): `frames`
 * frames `intervalMs` apart, each drawn at full size and scored, and only the
 * sharpest encoded, as grabFrame's JPEG. {blob, index, scores}.
 */
export async function grabBurst(video, {
  frames = BURST.frames[0], intervalMs = BURST.intervalMs, maxSide = 2048, quality = 0.88, waitMs = 3000, gain = 1,
} = {}) {
  await firstFrame(video, waitMs);
  const kept = await captureBurst({
    frames,
    intervalMs,
    take: () => {
      const canvas = drawFrame(video, { maxSide, gain });
      // A canvas let go of gives its memory back at once on a phone.
      canvas.release = () => { canvas.width = 0; canvas.height = 0; };
      return canvas;
    },
    score: sharpnessOf,
  });
  return { blob: await encode(kept.frame, quality), index: kept.index, scores: kept.scores };
}

/**
 * A small JPEG data URL of an image blob, for the gallery once the photos
 * themselves have gone to the server. Null when the browser cannot decode
 * it: a card without a picture is better than a capture that fails.
 */
export async function thumbnailOf(blob, size = 240) {
  if (!blob || typeof document === 'undefined' || typeof URL.createObjectURL !== 'function') return null;
  const url = URL.createObjectURL(blob);
  try {
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
    const scale = Math.min(1, size / Math.max(image.naturalWidth || 1, image.naturalHeight || 1));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.7);
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}
